import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Stack } from '@mui/material'
import { PlanStoreyPolicy } from '@api/fastAPI.schemas'
import { useUpdatePositionPlanSettings } from '@api/position-plan-settings/position-plan-settings'
import { useUpdateWallPlanSettings } from '@api/wall-plan-settings/wall-plan-settings'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form2, FormControlledSwitchLabeled, SubmitButton } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import { getModel } from '@queries'
import {
  updateBeam,
  updateColumn,
  updatePurlin,
  updateVerticalRoofSlab,
  updateVerticalSlab,
} from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import PlanSettingsDirtyBlocker from './PlanSettingsDirtyBlocker'
import PlanSettingsFields from './PlanSettingsFields'
import { planSettingsSchema } from './schema'
import {
  defaultPlanSettingsValues,
  isWall,
  isWallPosition,
  PlanSettingsElement,
  planSettingsElementId,
  PlanSettingsFormValues,
  storeysForPlansToPersist,
} from './utils'

interface Props<T extends PlanSettingsElement> {
  element: T
  /** Marks the element as changed in its element history, where the panel keeps one. */
  setStale?: (element: T) => void
}

const SAVE_ERROR_MESSAGE = 'Fehler beim Speichern der Einstellungen'

const isVerticalSlab = (element: PlanSettingsElement): element is VerticalSlab =>
  element.element_type === 'vertical_slabs'

const updateRequestFor = (element: PlanSettingsElement) => {
  switch (element.element_type) {
    case 'vertical_roof_slabs':
      return updateVerticalRoofSlab
    case 'beams':
      return updateBeam
    case 'purlins':
      return updatePurlin
    case 'columns':
      return updateColumn
    default:
      return updateVerticalSlab
  }
}

const PlanSettingsForm = <T extends PlanSettingsElement>({
  element,
  setStale,
}: Props<T>): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const invalidateResults = useResultsInvalidation()
  const defaultValues = useMemo(() => defaultPlanSettingsValues(element), [element])
  const elementId = planSettingsElementId(element)

  const onSaved = async () => {
    await client.invalidateQueries(getModel.getKey(projectId))
    invalidateResults(projectId as string)
    enqueueSnackbar('Einstellungen erfolgreich gespeichert', { variant: 'success' })
  }
  const onSaveError = (error: AxiosError) => {
    enqueueSnackbar(buildErrorMessage(error, SAVE_ERROR_MESSAGE), { variant: 'error' })
  }

  const { mutateAsync: persistSettings, isLoading: isSubmittingElement } = useMutation(
    async (next: T) => {
      await updateRequestFor(next).request(projectId, next)
      return next
    },
    {
      onSuccess: async (saved: T) => {
        setStale?.(saved)
        await onSaved()
      },
      onError: onSaveError,
    },
  )

  // Rips and lintels are stored in the wall positions: only their settings are sent.
  const { mutateAsync: persistPositionSettings, isLoading: isSubmittingPosition } =
    useUpdatePositionPlanSettings({
      mutation: {
        onSuccess: onSaved,
        onError: onSaveError,
      },
    })

  // Walls are stored with their geometry: only their settings are sent.
  const { mutateAsync: persistWallSettings, isLoading: isSubmittingWall } =
    useUpdateWallPlanSettings({
      mutation: {
        onSuccess: onSaved,
        onError: onSaveError,
      },
    })

  return (
    <Form2<PlanSettingsFormValues>
      id={`element-settings-${elementId}`}
      defaultValues={defaultValues}
      validationSchema={planSettingsSchema}
      reinitialize={true}
      resetIsDirtyAfterSubmit
      onSubmit={async values => {
        const withPlanStoreys = <S extends { planStoreyPolicy: PlanStoreyPolicy }>(
          settings: S,
        ): S => ({
          ...settings,
          planStoreyPolicy: values.planStoreyPolicy,
          storeysForPlans: storeysForPlansToPersist(values),
        })

        if (isWallPosition(element)) {
          await persistPositionSettings({
            projectGuid: projectId as string,
            positionGuid: element.position_guid,
            data: {
              positionPlanSettings: withPlanStoreys(element.positionPlanSettings),
              certificateSettings: element.certificateSettings ?? {},
            },
          })
          return
        }

        if (isWall(element)) {
          const settings = {
            positionPlanSettings: withPlanStoreys(element.positionPlanSettings),
            certificateSettings: element.certificateSettings ?? {},
          }
          await persistWallSettings({
            projectGuid: projectId as string,
            wallGuid: element.guid,
            data: settings,
          })
          setStale?.({ ...element, ...settings })
          return
        }

        const withPlanSettings = {
          ...element,
          positionPlanSettings: withPlanStoreys(element.positionPlanSettings),
          certificateSettings: element.certificateSettings ?? {},
        }

        if (isVerticalSlab(element)) {
          await persistSettings({
            ...withPlanSettings,
            metadata: {
              ...element.metadata,
              use_global_live_loads: values.use_global_live_loads ?? true,
            },
          })
          return
        }

        await persistSettings(withPlanSettings)
      }}
      data-cy="element-settings-form"
    >
      <PlanSettingsDirtyBlocker elementGuid={elementId} />
      <Stack spacing={1}>
        {isVerticalSlab(element) && (
          <FormControlledSwitchLabeled
            name="use_global_live_loads"
            label="Globale Nutzlasten ansetzen"
            data-cy="settings-global-live-loads"
          />
        )}
        <PlanSettingsFields />
        <Stack direction="row" justifyContent="end">
          <SubmitButton
            loading={isSubmittingElement || isSubmittingPosition || isSubmittingWall}
            data-cy="element-settings-submit"
          />
        </Stack>
      </Stack>
    </Form2>
  )
}

export default PlanSettingsForm
